import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { getSalesDashboard } from "../../../utils/services/sales";
import KpiRow from "./KpiRow";
import RevenueChart from "./RevenueChart";
import StatusChart from "./StatusChart";
import PaymentTypeChart from "./PaymentTypeChart";
import ProductsTable from "./ProductsTable";
import InstallmentsChart from "./InstallmentsChart";
import CouponsTable from "./CouponsTable";

// Dashboard de Vendas do evento.
//
// Tudo vem de uma chamada só (`/events/:id/payments/dashboard`), agregada no
// banco pela admin-api. Até aqui esta tela era um desenho: pizza, linha, mapa e
// extrato estavam com valores escritos no código, e nenhum deles mudava de
// evento para evento.
//
// Os números são do evento inteiro, não de um período. O recorte de tempo que
// interessa está no gráfico de receita acumulada, que mostra o ritmo sem mexer
// nos totais que a operação compara com o contrato.

const CardBlock = ({ title, subtitle, children }) => (
  <Card className="card-shadow">
    <CardBody>
      <div style={{ fontSize: "15px", fontWeight: 600, color: "#1f2937" }}>{title}</div>
      {subtitle && (
        <div style={{ marginTop: "2px", marginBottom: "12px", fontSize: "12.5px", color: "#74788d" }}>
          {subtitle}
        </div>
      )}
      {children}
    </CardBody>
  </Card>
);

function DashTupperware(props) {
  const location = useLocation();
  const token = props.state.global_user_data.data.token;
  const eventId = location.state?.idEvent;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const loadDashboard = useCallback(() => {
    if (!eventId) {
      setIsLoading(false);
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    getSalesDashboard({ jwt: token, eventId })
      .then((response) => {
        // O serviço devolve `error.response` quando a chamada falha, e aí não há
        // `summary`. Sem este guard a tela quebraria inteira num erro de rede.
        if (response?.summary) setData(response);
        else setHasError(true);
      })
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [token, eventId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (isLoading) {
    return (
      <Card className="card-shadow">
        <CardBody className="text-center py-5 text-muted">Carregando as vendas do evento...</CardBody>
      </Card>
    );
  }

  if (hasError || !data) {
    return (
      <Card className="card-shadow">
        <CardBody className="text-center py-5">
          <div className="text-muted mb-3">Não foi possível carregar as vendas deste evento.</div>
          <button type="button" className="btn btn-primary" onClick={loadDashboard}>
            Tentar de novo
          </button>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <KpiRow summary={data.summary} />

      <Row>
        <Col xs="12">
          <CardBlock
            title="Receita acumulada"
            subtitle="soma das transações pagas, dia a dia, no horário de Brasília"
          >
            <RevenueChart daily={data.daily} />
          </CardBlock>
        </Col>
      </Row>

      <Row>
        <Col xs="12" lg="6">
          <CardBlock
            title="Onde as compras param"
            subtitle="todas as transações abertas do evento, por situação"
          >
            <StatusChart
              status={data.status}
              openedTransactions={data.summary?.openedTransactions}
            />
          </CardBlock>
        </Col>
        <Col xs="12" lg="6">
          <CardBlock
            title="Meio de pagamento"
            subtitle="receita paga e quanto de cada meio vira pagamento de fato"
          >
            <PaymentTypeChart paymentTypes={data.paymentTypes} />
          </CardBlock>
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <CardBlock
            title="Produtos"
            subtitle="ocupação, receita e o que saiu nos últimos 7 dias"
          >
            <ProductsTable products={data.products} />
          </CardBlock>
        </Col>
      </Row>

      <Row>
        <Col xs="12" lg="6">
          <CardBlock
            title="Parcelamento"
            subtitle="receita no cartão por número de parcelas — é o que define quando o dinheiro entra"
          >
            <InstallmentsChart installments={data.installments} />
          </CardBlock>
        </Col>
        <Col xs="12" lg="6">
          <CardBlock
            title="Cupons"
            subtitle="desconto concedido e receita que veio junto, em vendas pagas"
          >
            <CouponsTable coupons={data.coupons} />
          </CardBlock>
        </Col>
      </Row>
    </>
  );
}

const mapStateToProps = (state) => {
  return { state };
};

export default connect(mapStateToProps)(DashTupperware);
